import React, { useEffect, useState } from 'react';
import getConfig from 'next/config';
import { useRouter } from 'next/compat/router';
import { MediaQueryProvider } from 'react-media-query-hoc';
import { Breadcrumbs } from '@domain-group/fe-co-header';
import { Paginator } from '@domain-group/fe-blueprint-ui/pagination';
import SearchResultsHead from '../head/search-result-head';
import SearchResultsFooterWithFragment from '../footer/search-results-footer-with-fragment';
import { UserDetailsRedacted } from '../../@types';
import { mapSearchResultsBreadcrumbs } from '../header/utils';
import {
  useRouteChangeLoading,
  useSearchResults,
  useSearchState,
} from './hooks';
import SearchAutocomplete from '../search-autocomplete';
import { Option } from '../search-autocomplete/types';
import {
  AgentTypeFilter,
  AppraisalCtaCard,
  Loader,
  LoadingOverlay,
  RmaDisclaimer,
  SearchResultCards,
  SearchTypeToggle,
  SortBySelect,
} from './components';
import {
  buildUrl,
  buildPaginatorUrl,
  formatResultsHeading,
  isPropertyManagerFilter,
} from './utils';
import {
  SALES_AGENT,
  DEFAULT_SORT_LABEL,
  DEFAULT_SORT_VALUE,
  SORT_OPTIONS_AGENCIES,
  SORT_OPTIONS_AGENTS,
  AGENCY_VIEW,
} from './components/search-filter-bar/enums';
import { trackSelectSortOrder } from '../../tracking/search-results';
import * as styles from './search-results-2026.style';

const { publicRuntimeConfig } = getConfig();

/* eslint-disable react/no-unused-prop-types */
export interface SearchResults2026Props {
  mixpanelHasInitialized?: boolean;
  isEmbeddedApp?: boolean;
  skipLinkContentId: string;
  user?: UserDetailsRedacted;
  sortBy?: string;
  keywordName?: string;
  agentFilter?: string;
  page: string;
  isAgency: boolean;
  suburbs: string;
}
/* eslint-enable react/no-unused-prop-types */

const SearchResults2026 = ({
  isEmbeddedApp = false,
  skipLinkContentId,
  user,
  page: initialPage,
  keywordName: initialKeyword,
  agentFilter: initialAgentFilter,
  sortBy: initialSortBy,
  isAgency: initialIsAgency,
  suburbs: initialSuburbs,
}: SearchResults2026Props): JSX.Element => {
  const router = useRouter();
  const routeLoading = useRouteChangeLoading(router);

  const state = useSearchState({
    isAgency: initialIsAgency,
    suburbs: initialSuburbs,
    page: initialPage,
    keywordName: initialKeyword,
    agentFilter: initialAgentFilter,
    sortBy: initialSortBy,
  });

  const { data, loading, error, actualLocationProfiles, search, agencyIds } =
    useSearchResults(state);

  const [isPending, setIsPending] = useState(false);
  const buildStateKey = (searchState: typeof state): string =>
    searchState
      ? [
          searchState.isAgency,
          searchState.page,
          searchState.sortBy,
          searchState.agentFilter,
          searchState.keywordName,
          searchState.suburbs,
        ].join('|')
      : '';
  const stateKey = buildStateKey(state);
  useEffect(() => {
    setIsPending(false);
  }, [stateKey]);

  useEffect(() => {
    if (!routeLoading) {
      setIsPending(false);
    }
  }, [routeLoading]);

  const hasNoSuburbs = !loading && (!state || !actualLocationProfiles.length);

  useEffect(() => {
    document.body.setAttribute('data-theme-name', 'domain-2022');
    return () => {
      document.body.removeAttribute('data-theme-name');
    };
  }, []);

  // Can't redirect during getServerSideProps, so it happens once on the client
  useEffect(() => {
    if (hasNoSuburbs) {
      router?.push?.('/real-estate-agents');
    }
  }, [hasNoSuburbs, router]);

  if (error) {
    throw new Error(`Something went wrong, ${error}`);
  }

  if (!data || !state || !actualLocationProfiles.length) {
    return <Loader />;
  }

  const { isAgency, page } = state;

  const breadcrumbs = mapSearchResultsBreadcrumbs({
    isAgent: !isAgency,
    suburbs: actualLocationProfiles,
  });

  const changePage = (filters: Partial<typeof state>): void => {
    const newState = { ...state, ...filters };
    const { to, as } = buildUrl(newState);
    const keepsCurrentSuburbs = typeof filters.suburbs === 'undefined';
    if (buildStateKey(newState) !== stateKey) {
      setIsPending(true);
    }
    router?.push(to, as, { shallow: keepsCurrentSuburbs });
  };

  const handlePageChange = (
    event: React.SyntheticEvent,
    newPage: number,
  ): void => {
    event.preventDefault();

    window?.scrollTo({ top: 0, behavior: 'smooth' });

    changePage({ page: newPage });
  };

  const setSortBy = (sortBy: string): void => {
    const sortOptions = isAgency ? SORT_OPTIONS_AGENCIES : SORT_OPTIONS_AGENTS;
    const sortOption = sortOptions.find((option) => option.value === sortBy);

    trackSelectSortOrder(sortOption?.label ?? DEFAULT_SORT_LABEL);

    changePage({ sortBy: sortOption?.value ?? DEFAULT_SORT_VALUE });
  };

  const initialSelectedOption: Option[] = actualLocationProfiles.map(
    (profile) => ({
      category: 'Suburb' as Option['category'],
      label: `${profile?.suburbName}, ${profile?.state} ${profile?.postcode}`,
      value: profile?.urlSlug ?? '',
      url: profile?.urlSlug ?? '',
      redirectToURL: false,
    }),
  );

  const selectedSuburbsKey = initialSelectedOption
    .map(({ value }) => value)
    .join(',');

  const selectSuburbs = (options: Option[]): void => {
    changePage({ suburbs: options.map(({ value }) => value) });
  };

  const selectAgentType = (agentFilter: string): void => {
    changePage({ agentFilter });
  };

  const isLoading = !!(loading || routeLoading || isPending);

  const selectSearchType = (view: string): void => {
    changePage({
      isAgency: view === AGENCY_VIEW,
      // Switching view resets the filters that do not apply across both
      page: 1,
      agentFilter: SALES_AGENT,
      keywordName: '',
      sortBy: DEFAULT_SORT_VALUE,
    });
  };

  return (
    <MediaQueryProvider values={{ width: 300, type: 'screen' }}>
      <div css={styles.page} data-theme-name="domain-2022">
        <SearchResultsHead
          isAgency={isAgency}
          total={search?.total}
          totalPages={search?.totalPages}
          pageNumber={page}
          suburbOptions={actualLocationProfiles}
          suburbSlugsArray={state.suburbs}
          sortBy={state.sortBy}
          agentType={state.agentFilter}
          keyword={state.keywordName}
          breadcrumbs={breadcrumbs}
          agencyIds={agencyIds}
          user={user}
          agentResults={data?.contactSearchBySuburbs?.results}
          agencyResults={data?.agencySearchBySuburbs?.results}
        />
        <div css={styles.breadcrumbBar}>
          <div css={[styles.wrapper, styles.breadcrumbs]}>
            <Breadcrumbs
              baseUrl={publicRuntimeConfig.BASE_URL}
              breadcrumbs={breadcrumbs}
              backButtonLabel="Back"
            />
          </div>
        </div>
        <div css={styles.searchBar}>
          <div css={[styles.wrapper, styles.searchBarInner]}>
            <div css={styles.typeahead}>
              <SearchAutocomplete
                key={selectedSuburbsKey}
                useTypeaheadV5
                isLocationSearchByAgent
                recentSearches
                suggestedSuburbs
                initialSelectedOption={initialSelectedOption}
                performSearchQuery={selectSuburbs}
              />
            </div>
            <div css={styles.filterGroup}>
              <SearchTypeToggle
                isAgency={isAgency}
                onSelect={selectSearchType}
              />
              {!isAgency && (
                <AgentTypeFilter
                  currentAgentType={state.agentFilter}
                  onSelect={selectAgentType}
                />
              )}
            </div>
          </div>
        </div>
        <main
          id={skipLinkContentId}
          data-testid="trade-search-results-2026__page"
        >
          <div css={styles.wrapper}>
            <div css={styles.grid}>
              <div css={styles.headingSection}>
                <div css={styles.headingRow}>
                  <h1 css={styles.heading} aria-live="polite">
                    {formatResultsHeading({
                      total: search?.total,
                      isAgency,
                      locations: actualLocationProfiles,
                    })}
                  </h1>
                  <SortBySelect
                    isAgency={isAgency}
                    selectedSortBy={state.sortBy}
                    setSortBy={setSortBy}
                  />
                </div>
                <RmaDisclaimer />
              </div>
              <div
                css={styles.results}
                data-testid="search-results-2026__results"
              >
                <LoadingOverlay
                  isLoading={isLoading}
                  contentCss={styles.resultsContent}
                >
                  <SearchResultCards
                    isAgency={isAgency}
                    agentResults={data?.contactSearchBySuburbs?.results}
                    agencyResults={data?.agencySearchBySuburbs?.results}
                    suburbName={
                      actualLocationProfiles[0]?.suburbName ?? undefined
                    }
                    isPropertyManager={isPropertyManagerFilter(
                      state.agentFilter,
                    )}
                  />
                </LoadingOverlay>
              </div>
              <div
                css={[styles.pagination, isLoading && styles.dimmed]}
                data-testid="search-paginator"
                aria-hidden={isLoading || undefined}
              >
                <Paginator
                  totalPages={search?.totalPages ?? 0}
                  currentPage={page}
                  urlTemplate={buildPaginatorUrl(state)}
                  onPageChange={handlePageChange}
                />
              </div>
              <aside css={styles.rail}>
                <AppraisalCtaCard
                  baseUrl={publicRuntimeConfig.BASE_URL}
                  isAgency={isAgency}
                />
              </aside>
            </div>
          </div>
        </main>
        {isEmbeddedApp ? null : (
          <SearchResultsFooterWithFragment
            id={
              actualLocationProfiles.length === 1
                ? actualLocationProfiles[0]?.id
                : null
            }
          />
        )}
      </div>
    </MediaQueryProvider>
  );
};

export default SearchResults2026;
